import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useTranslation } from 'react-i18next';
import styles from '../CustomMonthDatePicker/CustomMonthDatePicker.module.scss';

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;
/** After 2 digits, wait briefly so typing "2026" isn't cut off at "20"; then auto-fetch like 4-digit. */
const TWO_DIGIT_YEAR_COMMIT_MS = 350;

const toUtcEndOfYear = (year) =>
    new Date(Date.UTC(year, 11, 31, 23, 59, 59, 0));

const parseDate = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatYear = (value) => {
    const date = parseDate(value);
    return date ? String(date.getUTCFullYear()) : '';
};

const digitsOnly = (value, maxLength = 4) => String(value ?? '').replace(/\D/g, '').slice(0, maxLength);

/** Map "26" → 2026 (prefer 20xx, then 19xx) within YEAR_MIN..YEAR_MAX. */
const expandTwoDigitYear = (raw) => {
    if (!/^\d{2}$/.test(raw)) return null;
    const yy = Number(raw);
    for (const century of [2000, 1900]) {
        const expanded = century + yy;
        if (expanded >= YEAR_MIN && expanded <= YEAR_MAX) return expanded;
    }
    return null;
};

const parseYearNumber = (raw) => {
    const digits = digitsOnly(raw);
    if (/^\d{4}$/.test(digits)) {
        const nextYear = Number(digits);
        if (nextYear < YEAR_MIN || nextYear > YEAR_MAX) return null;
        return nextYear;
    }
    return expandTwoDigitYear(digits);
};

const parseYearInput = (raw) => {
    const year = parseYearNumber(raw);
    return year == null ? null : toUtcEndOfYear(year);
};

/**
 * YYYY year picker, same chrome as CustomMonthDatePicker.
 * API: date + onDateChange (UTC end of selected year).
 */
function CustomYearDatePicker({
    date,
    onDateChange,
    label,
    direction,
    height = '40px',
    width = '100%',
    borderRadius,
    disabled = false,
    'data-testid': dataTestId,
}) {
    const { i18n } = useTranslation();
    const dir = direction || i18n.dir();
    const current = parseDate(date);
    const selectedYear = current ? current.getUTCFullYear() : new Date().getUTCFullYear();

    const rootRef = useRef(null);
    const listRef = useRef(null);
    const scrolledToSelectedRef = useRef(false);
    const justFocusedRef = useRef(false);
    const yearCommitTimerRef = useRef(null);
    const inputValueRef = useRef('');

    const [inputValue, setInputValue] = useState(formatYear(date));
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    inputValueRef.current = inputValue;

    const clearYearCommitTimer = () => {
        if (yearCommitTimerRef.current == null) return;
        clearTimeout(yearCommitTimerRef.current);
        yearCommitTimerRef.current = null;
    };

    useEffect(() => () => clearYearCommitTimer(), []);

    useEffect(() => {
        if (!focused) setInputValue(formatYear(date));
    }, [date, focused]);

    const yearOptions = useMemo(
        () =>
            Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, index) => {
                const value = YEAR_MIN + index;
                return { value, label: String(value) };
            }).reverse(),
        [],
    );

    const updateMenuPosition = () => {
        const el = rootRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportPad = 8;
        const maxHeight = 220;
        const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
        const spaceAbove = rect.top - viewportPad;
        const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
        const heightPx = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow);

        setMenuStyle({
            position: 'fixed',
            top: openUp ? undefined : rect.bottom + 6,
            bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(heightPx, 96),
            zIndex: 10005,
        });
    };

    useLayoutEffect(() => {
        scrolledToSelectedRef.current = false;
        if (!open) {
            setMenuStyle(null);
            return undefined;
        }
        updateMenuPosition();
        const onReposition = (event) => {
            if (event?.target && listRef.current?.contains(event.target)) return;
            updateMenuPosition();
        };
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event) => {
            const inRoot = rootRef.current?.contains(event.target);
            const inList = listRef.current?.contains(event.target);
            if (!inRoot && !inList) setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const applyYear = (raw, { revertIfInvalid = false } = {}) => {
        clearYearCommitTimer();
        const parsed = parseYearInput(raw);
        if (!parsed) {
            if (revertIfInvalid) setInputValue(formatYear(date));
            return;
        }
        const nextYear = parsed.getUTCFullYear();
        setInputValue(String(nextYear));
        inputValueRef.current = String(nextYear);
        if (nextYear !== selectedYear) {
            onDateChange?.(parsed);
        }
    };

    const handleYearInputChange = (raw) => {
        clearYearCommitTimer();
        setInputValue(raw);
        inputValueRef.current = raw;

        if (raw.length === 4) {
            const nextYear = parseYearNumber(raw);
            if (nextYear != null && nextYear !== selectedYear) {
                applyYear(raw);
            }
            return;
        }

        if (raw.length === 2) {
            yearCommitTimerRef.current = setTimeout(() => {
                yearCommitTimerRef.current = null;
                if (inputValueRef.current !== raw) return;
                applyYear(raw);
            }, TWO_DIGIT_YEAR_COMMIT_MS);
        }
    };

    const openPicker = () => {
        if (disabled) return;
        setOpen((currentOpen) => !currentOpen);
    };

    const handleYearSelect = (year) => {
        applyYear(String(year));
        setOpen(false);
    };

    const list =
        open && menuStyle
            ? createPortal(
                <div
                    ref={listRef}
                    className={styles.listPortal}
                    role="listbox"
                    style={menuStyle}
                >
                    {yearOptions.map((option) => {
                        const selected = option.value === selectedYear;
                        return (
                            <button
                                type="button"
                                key={String(option.value)}
                                role="option"
                                aria-selected={selected}
                                className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleYearSelect(option.value)}
                                ref={(node) => {
                                    if (selected && node && !scrolledToSelectedRef.current) {
                                        scrolledToSelectedRef.current = true;
                                        node.scrollIntoView({ block: 'center', inline: 'nearest' });
                                    }
                                }}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>,
                document.body,
            )
            : null;

    return (
        <div
            className={`${styles.wrap} ${label ? styles.wrapWithLabel : ''} ${dir === 'rtl' ? styles.rtl : styles.ltr} ${disabled ? styles.disabled : ''}`}
            style={{ width }}
            data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        >
            <div
                ref={rootRef}
                className={`${styles.field} ${open ? styles.fieldOpen : ''} ${focused ? styles.fieldFocused : ''}`}
            >
                {label ? <span className={styles.notchLabel}>{label}</span> : null}
                <div className={styles.trigger} style={{ height, minHeight: height, borderRadius }}>
                    <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={label || 'Select year'}
                        aria-expanded={open}
                        aria-haspopup="listbox"
                        disabled={disabled}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={openPicker}
                    >
                        <CalendarTodayOutlinedIcon className={styles.calendarIcon} />
                    </button>
                    <input
                        className={styles.valueInput}
                        value={inputValue}
                        placeholder="YYYY"
                        inputMode="numeric"
                        autoComplete="off"
                        disabled={disabled}
                        aria-label={label || 'Year'}
                        data-testid={dataTestId ? `${dataTestId}-input-field` : undefined}
                        onFocus={(event) => {
                            setFocused(true);
                            justFocusedRef.current = true;
                            event.target.select();
                            if (open) setOpen(false);
                        }}
                        onMouseUp={(event) => {
                            if (justFocusedRef.current) {
                                event.preventDefault();
                                justFocusedRef.current = false;
                            }
                        }}
                        maxLength={4}
                        onChange={(event) => handleYearInputChange(digitsOnly(event.target.value))}
                        onBlur={(event) => {
                            setFocused(false);
                            justFocusedRef.current = false;
                            applyYear(digitsOnly(event.target.value), { revertIfInvalid: true });
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                applyYear(digitsOnly(event.currentTarget.value), { revertIfInvalid: true });
                                event.currentTarget.blur();
                            }
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                const reverted = formatYear(date);
                                setInputValue(reverted);
                                event.currentTarget.value = reverted;
                                setOpen(false);
                                event.currentTarget.blur();
                            }
                        }}
                    />
                </div>
            </div>
            {list}
        </div>
    );
}

export default CustomYearDatePicker;
